import { useEffect, useState } from "react";
import { 
  User, Briefcase, Mail, Phone, Linkedin, Instagram, Github, 
  Plus, Trash2, RefreshCw, Layers, FileText, Check, ShieldCheck, AlertCircle 
} from "lucide-react";
import { fetchTeamMembers, createTeamMember, deleteTeamMember } from "../../lib/api";

const TeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Initial state fully matching backend key constraints
  const initialFormState = {
    name: "",
    role: "",
    short_bio: "",
    long_bio: "",
    image_url: "",
    email: "",
    phone: "",
    linkedin: "",
    instagram: "",
    github: "",
    display_order: 0,
    is_board: false,
    is_active: true,
  };

  const [form, setForm] = useState(initialFormState);

  const loadMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTeamMembers();
      setMembers(Array.isArray(data) ? data : data.teamMembers || []);
    } catch (err) {
      setError(err.message || "Registry synchronization failure.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      await createTeamMember(form);
      setSuccessMsg("Team member directory updated successfully.");
      setForm(initialFormState);
      await loadMembers();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      setError(err.message || "Unable to decrypt data payload execution path.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to purge this record?")) return;
    setSaving(true);
    setError(null);
    try {
      await deleteTeamMember(id);
      await loadMembers();
    } catch (err) {
      setError(err.message || "Purge request rejected by data schema safety levels.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full font-sans bg-slate-50 min-h-screen p-4 sm:p-6 lg:p-8 relative">
      {/* Background Decorative modern light blobs matching logo palette */}
      <div className="absolute top-[5%] left-[10%] w-[400px] h-[400px] bg-blue-400/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-[10%] right-[5%] w-[400px] h-[400px] bg-orange-400/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white/80 backdrop-blur-md p-6 rounded-2xl border border-slate-200/80 shadow-sm">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <ShieldCheck className="text-blue-600 w-5 h-5" /> Human Capital Registry
            </h1>
            <p className="mt-1 text-xs text-slate-500">
              Configure corporate positions, display matrix priority, and modify active node deployment arrays.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={loadMembers}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? "animate-spin" : ""}`} />
              Sync Directory
            </button>
          </div>
        </div>

        {/* Messaging Logs */}
        {error && (
          <div className="bg-rose-50 border border-rose-100 text-rose-700 px-4 py-3 rounded-xl text-xs font-medium flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}
        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-100 text-emerald-700 px-4 py-3 rounded-xl text-xs font-medium flex items-center gap-2">
            <Check className="w-4 h-4 shrink-0" /> {successMsg}
          </div>
        )}

        {/* Grid Split Content Layout */}
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-start">
          
          {/* Left Block: Modern Profile Initialization Form */}
          <section className="bg-white/80 backdrop-blur-md rounded-2xl border border-slate-200/80 p-6 shadow-md space-y-6">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-base font-bold text-slate-900">Provision Node Account</h2>
              <p className="text-xs text-slate-500">Inject structured personnel objects into the server matrix.</p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {/* Row 1: Identification */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <User size={14} className="text-slate-400" /> Full Identity Name *
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="E.g., Jane Doe"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium text-slate-800 placeholder:text-slate-400"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Briefcase size={14} className="text-slate-400" /> Operational Role *
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="E.g., Principal Engineer"
                    value={form.role}
                    onChange={(e) => handleChange("role", e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium text-slate-800 placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Row 2: Communication Infrastructure */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Mail size={14} className="text-slate-400" /> Secure Routing Mail
                  </label>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium text-slate-800 placeholder:text-slate-400"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Phone size={14} className="text-slate-400" /> Comms Endpoint ID
                  </label>
                  <input
                    type="text"
                    placeholder="+1 (555) 000-0000"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium text-slate-800 placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Row 3: Biographies */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Layers size={14} className="text-slate-400" /> Quick Index Summary (Short Bio)
                </label>
                <input
                  type="text"
                  placeholder="One sentence system baseline summary..."
                  value={form.short_bio}
                  onChange={(e) => handleChange("short_bio", e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium text-slate-800 placeholder:text-slate-400"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <FileText size={14} className="text-slate-400" /> Full Narrative Block (Long Bio)
                </label>
                <textarea
                  rows={3}
                  placeholder="Comprehensive profile log structure..."
                  value={form.long_bio}
                  onChange={(e) => handleChange("long_bio", e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium text-slate-800 placeholder:text-slate-400 resize-none"
                />
              </div>

              {/* Row 4: Image Content URL */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Vector Image Asset Target URL</label>
                <input
                  type="url"
                  placeholder="https://assets.indivtechnosoft.com/profiles/avatar.jpg"
                  value={form.image_url}
                  onChange={(e) => handleChange("image_url", e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium text-slate-800 placeholder:text-slate-400"
                />
              </div>

              {/* Row 5: Social Sync Array Links */}
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Linkedin size={12} className="text-blue-500" /> LinkedIn
                  </label>
                  <input
                    type="url"
                    placeholder="https://linkedin.com/in/..."
                    value={form.linkedin}
                    onChange={(e) => handleChange("linkedin", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Instagram size={12} className="text-pink-500" /> Instagram
                  </label>
                  <input
                    type="url"
                    placeholder="https://instagram.com/..."
                    value={form.instagram}
                    onChange={(e) => handleChange("instagram", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Github size={12} className="text-slate-800" /> GitHub
                  </label>
                  <input
                    type="url"
                    placeholder="https://github.com/..."
                    value={form.github}
                    onChange={(e) => handleChange("github", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none text-xs font-medium"
                  />
                </div>
              </div>

              {/* Row 6: Meta Parameters & Routing Flags */}
              <div className="grid gap-4 sm:grid-cols-3 bg-slate-50/80 p-3 rounded-xl border border-slate-100">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={form.display_order}
                    onChange={(e) => handleChange("display_order", parseInt(e.target.value, 10) || 0)}
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg outline-none text-xs font-medium"
                  />
                </div>

                <div className="flex items-center mt-5">
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.is_board}
                      onChange={(e) => handleChange("is_board", e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    Board Member Status
                  </label>
                </div>

                <div className="flex items-center mt-5">
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => handleChange("is_active", e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Active Gateway Node
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving || loading}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 active:scale-[0.99] text-white font-semibold text-xs rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-blue-600/15 transition-all disabled:opacity-60 group"
              >
                <Plus className="w-4 h-4 group-hover:scale-110 transition-transform" />
                {saving ? "Encrypting System Payload..." : "Execute Registry Injection"}
              </button>
            </form>
          </section>

          {/* Right Block: Active Infrastructure Nodes */}
          <section className="bg-white/80 backdrop-blur-md rounded-2xl border border-slate-200/80 p-6 shadow-md space-y-4 max-h-[85vh] overflow-y-auto">
            <div>
              <h2 className="text-base font-bold text-slate-900">Synchronized Database Nodes</h2>
              <p className="text-xs text-slate-500">Live configuration records currently queried from `team_members` view.</p>
            </div>

            {loading ? (
              <div className="py-12 text-center text-xs text-slate-400 font-medium">
                Fetching synchronized relational indexes...
              </div>
            ) : members.length === 0 ? (
              <div className="py-12 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                No data rows exist in target table sequence.
              </div>
            ) : (
              <div className="space-y-3.5">
                {members.map((member) => (
                  <div 
                    key={member.id} 
                    className={`p-4 bg-white rounded-xl border transition-all ${
                      member.is_active ? "border-slate-200 shadow-sm" : "border-slate-200/40 opacity-60 bg-slate-100/50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase font-bold tracking-wider text-orange-500 bg-orange-50 px-2 py-0.5 rounded-md">
                            {member.role || "Node Payload"}
                          </span>
                          {member.is_board && (
                            <span className="text-[10px] uppercase font-bold tracking-wider text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
                              Board
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400 font-mono">ID: {member.id}</span>
                        </div>
                        
                        <h3 className="text-sm font-bold text-slate-800">{member.name || "Undefined Signature"}</h3>
                        <p className="text-xs text-slate-500 font-medium flex items-center gap-1">{member.email || "No configured email routing"}</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDelete(member.id)}
                        disabled={saving}
                        className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors border border-transparent hover:border-rose-100"
                        title="Purge Object Row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Inline Metadata Logs */}
                    {(member.short_bio || member.display_order !== undefined) && (
                      <div className="mt-3 pt-2.5 border-t border-slate-100 text-[11px] text-slate-600 space-y-1">
                        {member.short_bio && <p className="italic">"{member.short_bio}"</p>}
                        <div className="flex gap-3 text-[10px] text-slate-400 font-mono">
                          <span>Matrix Priority (Order): {member.display_order}</span>
                          <span>•</span>
                          <span>State: {member.is_active ? "ACTIVE_NODE" : "DISABLED"}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TeamMembers;